import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  Camera,
  Color,
  Euler,
  Group,
  LinearFilter,
  Material,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PerspectiveCamera,
  Quaternion,
  RGBAFormat,
  Scene,
  Sphere,
  SphereGeometry,
  Vector2,
  Vector3,
  WebGLRenderTarget,
  WebGLRenderer,
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { Pass } from "three/examples/jsm/postprocessing/Pass";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

import { ACESFilmicToneMappingShader } from "three/examples/jsm/shaders/ACESFilmicToneMappingShader.js";
import { GammaCorrectionShader } from "three/examples/jsm/shaders/GammaCorrectionShader.js";

import { EngineRequest } from "./EngineRequest";
import { CSS2DRenderer } from "./renderer/CSS2DRenderer";
import { CSS3DRenderer } from "./renderer/CSS3DRenderer.js";

import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { CAMERA_NEAR, CAMERA_FAR } from "./camera/CameraConstants";
import { IRenderer } from "./renderer/IRenderer";
import { WebGLRendererSingleton } from "./renderer/WebGLRendererSingleton";
import { logger } from "../util/Logger";
import {
  MaterialGeometryCache,
  MaterialGeometryInvalidationMode,
} from "./MaterialGeometryCache";
import {
  listActiveSceneMaterials,
  logActiveSceneMaterialsToConsole as logActiveSceneMaterialsToConsoleImpl,
  summarizeActiveSceneMaterials,
} from "./ActiveSceneMaterials";
import { RenderSceneSwapCoordinator } from "./RenderSceneSwapCoordinator";
import { SceneMutationObserver } from "./SceneMutationObserver";
import { frameProfiler } from "./FrameProfiler";
import { engineProfiling, profile } from "./EngineProfiling";

export { MaterialGeometryCache, MaterialGeometryInvalidationMode } from "./MaterialGeometryCache";
export { RenderSceneReparentManager } from "./RenderSceneReparent";
export { RenderSceneSwapCoordinator } from "./RenderSceneSwapCoordinator";
export {
  listActiveSceneMaterials,
  summarizeActiveSceneMaterials,
  logActiveSceneMaterialsToConsole,
  type ActiveSceneMaterialSummaryRow,
} from "./ActiveSceneMaterials";
export {
  logMaterialGeometryBuckets,
  logSceneToRenderTree,
  logRenderSceneDebugBundle,
} from "./RenderSceneDebugLog";

export class Engine extends EventTarget {
  private _scene: Scene | undefined;
  /** Sahneye eklenen geçici hata ayıklama mesh’leri (küp / küre). */
  private _debugLogGroup?: Group;
  private _newEntities: Entity[] = [];
  private _bindElement: HTMLElement | undefined;
  private _render_targets: RenderTarget[] = [];
  private _start = false;
  private _width: number = 512;
  private _height: number = 512;
  private _requestUpdate = true;
  private _requests?: { before: EngineRequest[]; after: EngineRequest[] } = {
    before: [],
    after: [],
  };

  private _camera?: CameraInterface;

  private _viewAngle = 25;
  private static _activeEngine: Engine;

  /** Materyal başına geometri listesi; sync her kare animate içinde çağrılır. */
  private readonly _materialGeometryCache = new MaterialGeometryCache();
  private _materialGeometryCacheRequestUpdateListening = false;
  private readonly _materialGeometryCacheOnRequestUpdate = () => {
    this._materialGeometryCache.markStale();
  };

  /** WebGL swap + reparent + render-scene debug tek yerde. */
  private readonly _renderSceneSwap = new RenderSceneSwapCoordinator();

  /**
   * Son _renderScene (composer veya dogrudan renderer.render) tamamlandiktan sonra
   * biriken cagri / ucgen; rAF ile renderer.info okumaktan dogru sonuc verir
   * (render() her kare basinda sayaclari sifirlar).
   */
  private _lastWebGLMainFrameStats: {
    calls: number;
    triangles: number;
  } | null = null;
  private _webglMainFrameSavedAutoReset: boolean | undefined;

  static get activeEngine() {
    return Engine._activeEngine;
  }

  private _transformControls?: TransformControls;

  get transformControls() {
    if (!this._transformControls) {
      this._transformControls = new TransformControls(
        this.activeCamera!.instance,
        this.canvas
      );
      this.activeScene!.add(this._transformControls.getHelper());
    }
    return this._transformControls;
  }

  constructor(bindElement?: HTMLElement) {
    super();
    Engine._activeEngine = this;
    this.renderer = WebGLRendererSingleton.getInstance();
    this.cssRenderer = new CSS2DRenderer();
    this.cssRenderer3d = new CSS3DRenderer();

    this._scene = new Scene();
    SceneMutationObserver.observe(this._scene, this);

    //this._shadowRoot = shadowRoot;
    if (bindElement) this.bindElement(bindElement);

    this._camera = new BasicCamera(this.width, this.height);

    //camera controllers
    this._camera?.instance.position.setZ(15);
    this._camera?.instance.position.setY(0);

    this.addEventListener("engine-request-before", (event: any) => {
      this.addBeforeRequest(event.detail);
    });

    this.addEventListener("engine-request-after", (event: any) => {
      this.addAfterRequest(event.detail);
    });

    this.addEventListener("request-update", (event: any) => {
      this._requestUpdate = true;
    });

    this.addEventListener("camera-change", (event: any) => {});

    this.addEventListener("camera-start", (_event: any) => {
      this._cameraActive = true;
      this._tryEnterInteraction();
    });
    this.addEventListener("camera-end", (_event: any) => {
      this._cameraActive = false;
      this._tryExitInteraction();
    });
  }

  private _pointerCount = 0;
  private _cameraActive = false;
  private _interactionActive = false;
  private _pointerListenersAdded = false;

  private _tryEnterInteraction() {
    if (this._interactionActive) return;
    if (this._pointerCount > 0 || this._cameraActive) {
      this._interactionActive = true;
      this.cssRendererVisible = false;
      const pr = Math.max(1, window.devicePixelRatio / 2);
      //this.renderer.instance.setDrawingBufferSize(this._width, this._height, pr);
      //this.renderer.instance.setPixelRatio(pr);
      //if (this._composer) this._composer.setPixelRatio(pr);
      this.dispatchCustomEvent("interaction-start");
    }
  }

  private _tryExitInteraction() {
    if (!this._interactionActive) return;
    if (this._pointerCount === 0 && !this._cameraActive) {
      this._interactionActive = false;
      this.cssRendererVisible = true;
      const pr = Math.max(2, window.devicePixelRatio);
      //this.renderer.instance.setDrawingBufferSize(this._width, this._height, pr);
      //this.renderer.instance.setPixelRatio(pr);
      //if (this._composer) this._composer.setPixelRatio(pr);
      this.dispatchCustomEvent("interaction-end");
    }
  }

  private _onPointerDown = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this._pointerCount++;
    this._tryEnterInteraction();
  };

  private _onPointerUp = () => {
    this._pointerCount--;
    if (this._pointerCount < 0) this._pointerCount = 0;
    this._tryExitInteraction();
  };

  entities: Entity[] = [];
  renderer: IRenderer;

  cssRenderer: CSS2DRenderer;
  cssRendererAdded = false;

  cssRenderer3d: CSS3DRenderer;
  cssRenderer3dAdded = false;

  cssRendererVisible = true;

  get canvas() {
    return this.renderer.instance.domElement;
  }

  get activeScene(): Scene | undefined {
    return this._scene;
  }

  get activeCamera(): CameraInterface | undefined {
    return this._camera;
  }

  set activeCamera(value: CameraInterface) {
    this._camera = value;
    this.updateComposerEffect();
    this.dispatchEvent(new CustomEvent("update-camera"));
  }

  get viewAngle() {
    return this._viewAngle;
  }

  set viewAngle(value) {
    this._viewAngle = value;
  }

  get width() {
    return this._width;
  }

  get height() {
    return this._height;
  }

  get bindedElement() {
    return this._bindElement;
  }

  private _resizeObserver?: ResizeObserver;
  private _isResizing = false;

  bindElement = (element: HTMLElement | undefined) => {
    if (this._bindElement) this._resizeObserver?.unobserve(this._bindElement);
    if (!element && this._pointerListenersAdded) {
      const canvas = this.renderer.instance.domElement;
      canvas.removeEventListener("pointerdown", this._onPointerDown);
      canvas.removeEventListener("pointerup", this._onPointerUp);
      canvas.removeEventListener("pointercancel", this._onPointerUp);
      this._pointerListenersAdded = false;
    }
    this._bindElement = element;

    this.resize();
    if (!this._resizeObserver)
      this._resizeObserver = new ResizeObserver((entries) => {
        const cr = entries[0].contentRect;
        if (cr.width > 0 && cr.height > 0) {
          this.resize();
        }
      });

    this._resizeObserver?.observe(element!);
    this._bindElement?.appendChild(this.renderer.instance.domElement);

    this._bindElement?.appendChild(this.cssRenderer.domElement);
    this.cssRendererAdded = true;

    this._bindElement?.appendChild(this.cssRenderer3d.domElement);
    this.cssRenderer3dAdded = true;

    const canvas = this.renderer.instance.domElement;
    if (!this._pointerListenersAdded) {
      canvas.addEventListener("pointerdown", this._onPointerDown);
      canvas.addEventListener("pointerup", this._onPointerUp);
      canvas.addEventListener("pointercancel", this._onPointerUp);
      this._pointerListenersAdded = true;
    }
  };

  resize = () => {
    if (this._isResizing) return;
    this._isResizing = true;

    const _elementCheck = this._bindElement
      ? this._bindElement
      : this.renderer.instance.domElement;
    const { width, height } = _elementCheck.getBoundingClientRect();

    this._width = width ? width : 128;
    this._height = height ? height : 128;
    this.renderer.instance.domElement.width = this._width;
    this.renderer.instance.domElement.height = this._height;

    this.cssRenderer.setSize(this._width, this._height);
    this.cssRenderer3d.setSize(this._width, this._height);
    this.renderer.instance.setSize(this._width, this._height);
    const isMobile = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    this.renderer.instance.setDrawingBufferSize(this._width, this._height, 2);
    this._camera?.update(this._width, this._height);

    this.renderer.instance.domElement.remove();
    this.cssRenderer.domElement.remove();
    this.cssRenderer3d.domElement.remove();
    this._bindElement?.appendChild(this.renderer.instance.domElement);

    this._bindElement?.appendChild(this.cssRenderer.domElement);
    this.cssRendererAdded = true;

    this._bindElement?.appendChild(this.cssRenderer3d.domElement);
    this.cssRenderer3dAdded = true;
    if (this._composer) {
      const _pixelRatio = this.renderer.instance.getPixelRatio();
      this._composer.setSize(this._width, this._height);
      this._composer.setPixelRatio(_pixelRatio);
    }

    this.dispatchCustomEvent("engine-resize");

    this._requestUpdate = true;
    this._isResizing = false;
  };

  private _systems = new Map();
  addSystem(name: string, object: any) {
    this._systems.set(name, object);
  }

  getSystem(name: string): any {
    return this._systems.get(name);
  }

  /**
   *
   * @param entity
   */
  addEntity = (entity: Entity) => {
    //console.log("entity added", entity);
    try {
      this._newEntities.push(entity);
      this.entities.push(entity);
      entity[$engineKey] = this;
      entity.onAdd(this);
    } catch (e) {
      logger.error("****Engine", "handled error:", e);
    }
  };

  removeEntity = (entity: Entity) => {
    try {
      this.entities = this.entities.filter((e) => entity != e);
      entity[$engineKey] = null;
      entity.onRemove(this);
    } catch (e) {
      logger.error("****Engine", "handled error:", e);
    }
  };

  dispatchCustomEvent(event: string, object?: { detail?: any; event?: Event }) {
    this.dispatchEvent(new CustomEvent(event, object));
  }

  private getOrCreateDebugLogGroup(): Group | undefined {
    const scene = this._scene;
    if (!scene) return undefined;
    if (!this._debugLogGroup) {
      const g = new Group();
      g.name = "engine-debug-log";
      scene.add(g);
      this._debugLogGroup = g;
    }
    return this._debugLogGroup;
  }

  /**
   * Sahneye kucuk bir hata ayiklama mesh'i ekler (kure veya kup).
   * Ornek: Engine.activeEngine?.log("Sphere", worldPosition, worldQuaternion)
   *
   * @param shape "sphere" | "box" | "cube" (buyuk/kucuk harf duyarsiz; "spehere" yazimi da kabul edilir)
   * @param position Dunya konumu
   * @param rotation Istege bagli: Euler, Quaternion veya [x,y,z] (radyan, sira XYZ)
   */
  log(
    shape: string,
    position: Vector3 | readonly [number, number, number],
    rotation?: Euler | Quaternion | readonly [number, number, number],
  ): void {
    const group = this.getOrCreateDebugLogGroup();
    if (!group) return;

    const pos =
      position instanceof Vector3
        ? position.clone()
        : new Vector3(position[0], position[1], position[2]);

    const key = shape.trim().toLowerCase();
    const useSphere =
      key === "sphere" || key === "spehere" || key === "küre";
    const geom = useSphere
      ? new SphereGeometry(0.08, 16, 12)
      : new BoxGeometry(0.14, 0.14, 0.14);

    const hue = (group.children.length * 0.17) % 1;
    const mat = new MeshBasicMaterial({
      color: new Color().setHSL(hue, 0.85, 0.55),
      depthTest: true,
      transparent: true,
      opacity: 0.9,
    });

    const mesh = new Mesh(geom, mat);
    mesh.name = "engine-debug-" + shape + "-" + group.children.length;
    mesh.userData.engineDebugLog = true;
    mesh.position.copy(pos);

    if (rotation !== undefined) {
      if (rotation instanceof Quaternion) {
        mesh.setRotationFromQuaternion(rotation);
      } else if (rotation instanceof Euler) {
        mesh.rotation.copy(rotation);
      } else {
        mesh.rotation.set(rotation[0], rotation[1], rotation[2], "XYZ");
      }
    }

    group.add(mesh);
    this.dispatchEvent(new CustomEvent("request-update"));
  }

  /** log ile eklenen tum hata ayiklama mesh'lerini ve kaynaklarini kaldirir. */
  clearDebugLog(): void {
    const g = this._debugLogGroup;
    if (!g) return;
    while (g.children.length > 0) {
      const mesh = g.children[0] as Mesh;
      g.remove(mesh);
      mesh.geometry?.dispose();
      const m = mesh.material;
      if (Array.isArray(m)) m.forEach((x) => x.dispose());
      else (m as MeshBasicMaterial)?.dispose?.();
    }
    g.removeFromParent();
    this._debugLogGroup = undefined;
    this.dispatchEvent(new CustomEvent("request-update"));
  }

  /**
   * MaterialGeometryInvalidationMode bit birlesimi.
   * StructureSignature: mesh / geometry.uuid / material.uuid / visible degisince yenileme.
   * Explicit: ayrica yalnizca invalidateMaterialGeometryCache() ile tam tarama
   * (imza kapaliyken sahne donuk kalir).
   * Varsayilan onbellek modu: StructureSignature.
   */
  setMaterialGeometryInvalidationModes(
    modes: MaterialGeometryInvalidationMode,
  ): void {
    this._materialGeometryCache.setModes(modes);
  }

  getMaterialGeometryInvalidationModes(): MaterialGeometryInvalidationMode {
    return this._materialGeometryCache.getModes();
  }

  /** Sonraki sync tam sahne taramasi yapar. */
  invalidateMaterialGeometryCache(): void {
    this._materialGeometryCache.invalidate();
  }

  /** Son materyal → geometri listesi (referanslar sahne ile paylaşılır). */
  getMaterialGeometryBuckets(): ReadonlyMap<
    Material,
    readonly BufferGeometry[]
  > {
    return this._materialGeometryCache.getBuckets();
  }

  forEachMaterialGeometry(
    callback: (material: Material, geometries: readonly BufferGeometry[]) => void,
  ): void {
    this._materialGeometryCache.forEachMaterialGeometry(callback);
  }

  /**
   * activeScene icindeki mesh'lerde gecen benzersiz materyaller (sira: uuid).
   * MaterialGeometryCache ile ayni kurallar: Mesh / InstancedMesh, geometrisi olanlar;
   * Line / Points dahil degildir.
   */
  getActiveSceneMaterials(): Material[] {
    return listActiveSceneMaterials(
      this._materialGeometryCache,
      this.activeScene,
    );
  }

  getActiveSceneMaterialsSummary() {
    return summarizeActiveSceneMaterials(
      this._materialGeometryCache,
      this.activeScene,
    );
  }

  logActiveSceneMaterialsToConsole(label = "activeScene materials"): void {
    logActiveSceneMaterialsToConsoleImpl(
      this._materialGeometryCache,
      this.activeScene,
      label,
    );
  }

  /**
   * true iken her request-update onbellegi stale isaretler;
   * boylece sonraki karede modlara gore yeniden taranir.
   */
  setMaterialGeometryCacheListenRequestUpdate(bind: boolean): void {
    if (bind === this._materialGeometryCacheRequestUpdateListening) return;
    if (bind) {
      this.addEventListener(
        "request-update",
        this._materialGeometryCacheOnRequestUpdate as EventListener,
      );
    } else {
      this.removeEventListener(
        "request-update",
        this._materialGeometryCacheOnRequestUpdate as EventListener,
      );
    }
    this._materialGeometryCacheRequestUpdateListening = bind;
  }

  /**
   * true iken WebGL her karede mesh'leri materyal gruplarina reparent edip
   * activeSceneToRender uzerinden cizer; kare sonunda activeScene hiyerarsisine geri alir.
   * CSS2D/CSS3D activeScene uzerinde kalir (_renderTargets).
   */
  setUseRenderSceneSwap(value: boolean): void {
    this._renderSceneSwap.setEnabled(
      value,
      this._scene,
      (s) => this._setComposerRenderPassScene(s),
    );
    this.dispatchCustomEvent("webgl-render-scene-changed");
  }

  get useRenderSceneSwap(): boolean {
    return this._renderSceneSwap.enabled;
  }

  /**
   * Swap aciken materyal basina birlesik geometri -> tek draw call (CPU'da vertex kopyasi).
   * Animasyonlu donusumler imza yenilenmedikce guncellenmez; statik urun / konfiguratur icin uygundur.
   */
  setRenderSceneMergeMeshesPerMaterial(value: boolean): void {
    this._renderSceneSwap.setMergeMeshesPerMaterial(value);
  }

  get renderSceneMergeMeshesPerMaterial(): boolean {
    return this._renderSceneSwap.mergeMeshesPerMaterial;
  }

  /**
   * setUseRenderSceneSwap(true) iken activeSceneToRender icerigini ve bucket'lari konsola yazar.
   * @param logEachFrame her kare -- cok gurultulu.
   */
  setDebugLogRenderScene(enabled: boolean, logEachFrame = false): void {
    this._renderSceneSwap.setDebugLog(enabled, logEachFrame);
  }

  /**
   * Per-frame profiler. Açıkken her saniye özet tablo basar,
   * slowFrameMs'i aşan frame'lerde breakdown uyarısı atar.
   *
   * Konsoldan: window.__frameProfiler.setEnabled(true)
   * Parametrik config: window.__engineProfiling.setConfig({ perEntity: false, ... })
   */
  setFrameProfilerEnabled(
    enabled: boolean,
    options?: { slowFrameMs?: number; flushEveryMs?: number },
  ): void {
    if (options?.slowFrameMs != null) {
      frameProfiler.setSlowFrameThreshold(options.slowFrameMs);
    }
    if (options?.flushEveryMs != null) {
      frameProfiler.setFlushInterval(options.flushEveryMs);
    }
    frameProfiler.setEnabled(enabled);
  }

  get debugLogRenderScene(): boolean {
    return this._renderSceneSwap.debugLog;
  }

  get debugLogRenderSceneEachFrame(): boolean {
    return this._renderSceneSwap.debugEachFrame;
  }

  logRenderSceneStateNow(reason = "manual"): void {
    if (this.activeScene) {
      this._materialGeometryCache.sync(this.activeScene);
    }
    this._renderSceneSwap.logStateNow(
      this.activeScene,
      this._materialGeometryCache.getLastStructureSignature(),
      this._materialGeometryCache.getBuckets(),
      reason,
    );
  }

  get activeSceneToRender(): Scene {
    return this._renderSceneSwap.sceneToRender;
  }

  getSceneForWebGLRender(): Scene {
    return this._renderSceneSwap.getWebGLScene(this._scene!);
  }

  private _setComposerRenderPassScene(target: Scene): void {
    if (!this._composer) return;
    const renderPass = this._composer.passes.find(
      (p) => p instanceof RenderPass,
    ) as RenderPass | undefined;
    if (renderPass) renderPass.scene = target;
  }

  /**
   *
   * @param renderTarget
   */
  addRenderTarget(renderTarget: RenderTarget) {
    this._render_targets.push(renderTarget);
  }

  removeRenderTarget(renderTarget: RenderTarget | undefined) {
    this._render_targets = this._render_targets.filter(
      (e) => renderTarget != e
    );
  }

  /**
   * pass composer part
   */
  private _composer?: EffectComposer;
  private _gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
  private _acesFilmPass = new ShaderPass(ACESFilmicToneMappingShader);

  /**
   * ACES + gamma kuyruk gecisleri gecici cikarilir; callback icinde ara pass sirasi duzenlenir.
   * Composer yoksa olusturulur (getOrCreatePassComposer).
   */
  mutatePassComposer(mutate: (composer: EffectComposer) => void): void {
    const composer = this.getOrCreatePassComposer();
    composer.removePass(this._gammaCorrectionPass);
    composer.removePass(this._acesFilmPass);
    try {
      mutate(composer);
    } finally {
      composer.addPass(this._acesFilmPass);
      composer.addPass(this._gammaCorrectionPass);
      this._acesFilmPass.material.uniforms["exposure"].value = 1;
    }
  }

  /**
   * mutatePassComposer ile ayni; composer hic yaratilmadiysa no-op (dispose sonrasi vb.).
   */
  mutatePassComposerIfExists(mutate: (composer: EffectComposer) => void): void {
    if (!this._composer) return;
    const composer = this._composer;
    composer.removePass(this._gammaCorrectionPass);
    composer.removePass(this._acesFilmPass);
    try {
      mutate(composer);
    } finally {
      composer.addPass(this._acesFilmPass);
      composer.addPass(this._gammaCorrectionPass);
      this._acesFilmPass.material.uniforms["exposure"].value = 1;
    }
  }

  addPass(pass?: Pass) {
    if (!pass) return;
    const composer = this.getOrCreatePassComposer();
    composer.removePass(this._gammaCorrectionPass);
    composer.removePass(this._acesFilmPass);
    composer.addPass(pass);

    composer.addPass(this._acesFilmPass);
    composer.addPass(this._gammaCorrectionPass);

    this._acesFilmPass.material.uniforms["exposure"].value = 1;
  }

  removePass(pass?: Pass) {
    if (!pass) return;
    this._composer?.removePass(this._gammaCorrectionPass);
    this._composer?.removePass(this._acesFilmPass);
    this._composer?.removePass(pass);
    if (this._composer && this._composer!.passes.length < 2) {
      this._composer.dispose();
      this._composer = undefined;
    } else {
      this._composer?.addPass(this._acesFilmPass);
      this._composer?.addPass(this._gammaCorrectionPass);
    }
  }

  hasPass(pass?: Pass) {
    return this._composer?.passes.find((p) => p == pass);
  }

  private updateComposerEffect() {
    if (this._composer) {
      const renderPass = this._composer.passes.find(
        (p) => p instanceof RenderPass
      ) as RenderPass | undefined;
      if (renderPass) {
        renderPass.camera = this.activeCamera!.instance;
        if (!this._renderSceneSwap.enabled && this._scene) {
          renderPass.scene = this._scene;
        }
      }
    }
  }

  private getOrCreatePassComposer() {
    if (!this._composer) {
      const parameters = {
        minFilter: LinearFilter,
        magFilter: LinearFilter,
        format: RGBAFormat,
        stencilBuffer: false,
        samples: 0,
      };

      const size = this.renderer.instance.getSize(new Vector2());
      const _pixelRatio = this.renderer.instance.getPixelRatio();
      const w = size.width || this._width;
      const h = size.height || this._height;

      const renderTarget = new WebGLRenderTarget(
        w * _pixelRatio,
        h * _pixelRatio,
        parameters
      );
      renderTarget.texture.name = "SEffectComposer.rt1";
      this._composer = new EffectComposer(
        this.renderer!.instance as WebGLRenderer,
        renderTarget
      );
      this._composer.setPixelRatio(_pixelRatio);

      const pass = new RenderPass(
        this.activeScene!,
        this.activeCamera!.instance
      );
      this._composer.addPass(pass);
    }
    return this._composer;
  }

  /**
   * GUI
   */
  private _guiContainer?: any;
  async addGUIFolder(name: string) {
    if (!this._guiContainer) {
      const guiModule = await import("lil-gui");
      this._guiContainer = new guiModule.GUI({
        container: this.renderer.instance.domElement.parentElement!,
      });

      this._guiContainer.domElement.style.position = "absolute";
      this._guiContainer.domElement.style.top = "0px";
      this._guiContainer.domElement.style.right = "0px";

      //style stheet to shadow root
      const globalSheets = Array.from(document.styleSheets);
      if (globalSheets && globalSheets.length > 0) {
        const x = globalSheets[0];
        const sheet = new CSSStyleSheet();
        const css = Array.from(x.cssRules)
          .map((rule) => rule.cssText)
          .join(" ");
        sheet.replaceSync(css);
        //this.canvas?.parentElement.adoptedStyleSheets.push(sheet);
        //document.body.adoptedStyleSheets.push(sheet);
        //TO-DO
      }
    }
    return this._guiContainer?.addFolder(name);
  }


  private _paused = false;
  private _rafScheduled = false;
  private _rafScheduleCount = 0;
  private _animateBoundForRAF = (t: number) => {
    this._rafScheduled = false;
    this.animate(t);
  };
  private _scheduleNextFrame(): void {
    if (this._rafScheduled) return;
    this._rafScheduled = true;
    this._rafScheduleCount++;
    requestAnimationFrame(this._animateBoundForRAF);
  }
  pause() {
    this._paused = true;
  }
  resume() {
    this._paused = false;
    this._scheduleNextFrame();
  }
  isPaused() {
    return this._paused;
  }
  animate(dt: number) {
    if (this._paused) return;
    this._scheduleNextFrame();

    frameProfiler.beginFrame();

    if (SceneMutationObserver.consumeDirty(this)) {
      this.dispatchCustomEvent("scene-changed");
    }

    this._beginNewEntities();
    this._animateEntities(dt);
    this._beforeRenderEntities();
    this._syncMatGeomCache();
    this._onBeforeRender();
    this._renderTargets();

    engineProfiling.captureStructuralGauges({
      entitiesCount: this.entities.length,
      passesCount: this._composer?.passes.length ?? 0,
      renderTargetsCount: this._render_targets.length,
      rafScheduledTotal: this._rafScheduleCount,
      scene: this.activeScene ?? undefined,
    });

    this._renderScene();
    engineProfiling.captureGlGauges(this.renderer.instance);

    this._onAfterRender();

    frameProfiler.endFrame();
  }

  @profile("entities.begin")
  private _beginNewEntities(): void {
    const list = this._newEntities;
    if (list.length === 0) return;
    this._newEntities = [];
    for (const e of list) e.begin();
  }

  @profile("entities.animate")
  private _animateEntities(dt: number): void {
    for (const e of this.entities) {
      e.animate(dt, this.activeScene!, this.activeCamera!);
    }
  }

  @profile("entities.beforeRender")
  private _beforeRenderEntities(): void {
    for (const e of this.entities) e.beforeRender();
  }

  @profile("matGeomCache.sync")
  private _syncMatGeomCache(): void {
    this._materialGeometryCache.sync(this.activeScene);
  }

  @profile("renderScene")
  private _renderScene(): void {
    engineProfiling.instrumentComposer(this._composer);
    const logical = this.activeScene!;
    const useSwap = this._renderSceneSwap.enabled && !!logical;
    const sig = this._materialGeometryCache.getLastStructureSignature();
    const buckets = this._materialGeometryCache.getBuckets();

    const renderStructureRebuilt = useSwap
      ? this._renderSceneSwap.prepareFrame(logical, sig, buckets)
      : false;

    const glInst = this.renderer.instance;
    let webglMainFrameMeter = false;
    if (glInst instanceof WebGLRenderer) {
      this._webglMainFrameSavedAutoReset = glInst.info.autoReset;
      glInst.info.autoReset = false;
      glInst.info.reset();
      webglMainFrameMeter = true;
    } else {
      this._lastWebGLMainFrameStats = null;
    }

    try {
      if (useSwap) {
        this._renderSceneSwap.pushAndLog(
          renderStructureRebuilt,
          sig,
          buckets,
          (s) => this._setComposerRenderPassScene(s),
        );
      }

      if (this._composer) {
        this._composer.render();
      } else {
        this.renderer.render(
          this._renderSceneSwap.getWebGLScene(logical),
          this.activeCamera!.instance,
        );
      }

      if (webglMainFrameMeter && glInst instanceof WebGLRenderer) {
        this._lastWebGLMainFrameStats = {
          calls: glInst.info.render.calls,
          triangles: glInst.info.render.triangles,
        };
      }
    } finally {
      if (useSwap) {
        this._renderSceneSwap.popAndRestoreComposer(logical, (s) =>
          this._setComposerRenderPassScene(s),
        );
      }
      if (webglMainFrameMeter && glInst instanceof WebGLRenderer) {
        glInst.info.autoReset = this._webglMainFrameSavedAutoReset ?? true;
      }
    }
  }

  /**
   * Son ana sahne render geçişinin toplam draw call ve üçgen sayısı (RT geçişleri dahil değil).
   * addAfterRequest / engine-request-after ile kayitli callback'lerde okunmali (_onAfterRender sirasi).
   */
  getLastWebGLMainFrameStats(): {
    calls: number;
    triangles: number;
  } | null {
    return this._lastWebGLMainFrameStats;
  }

  @profile("onBeforeRender")
  private _onBeforeRender(): void {
    this._requests?.before.forEach((req) => {
      req.callback();
    });
    this._requests!.before = this._requests!.before.filter(
      (req) => !req.dispose
    );
  }

  @profile("onAfterRender")
  private _onAfterRender(): void {
    this._requests?.after.forEach((req) => {
      req.callback();
    });
    this._requests!.after = this._requests!.after.filter((req) => !req.dispose);
  }

  addBeforeRequest(req: EngineRequest) {
    this._requests?.before.push(req);
  }

  addAfterRequest(req: EngineRequest) {
    this._requests?.after.push(req);
  }

  start() {
    if (this._start) {
      logger.warn(
        "Engine.start() called multiple times — ignoring duplicate (prevents rAF loop leak)",
      );
      return;
    }
    this._start = true;
    this.animate(0);
  }

  private hasScene(scene: Object3D, statement: (obj: Object3D) => boolean) {
    if (statement(scene)) return true;

    for (var i = 0; i < scene.children.length; i++) {
      if (this.hasScene(scene.children[i], statement)) return true;
    }
    return false;
  }
  @profile("renderTargets")
  private _renderTargets() {
    if (this.cssRenderer instanceof CSS2DRenderer && this._requestUpdate) {
      if (
        this.hasScene(this.activeScene!, (element: Object3D) => {
          return (element as any).isCSS2DObject;
        }) &&
        this.cssRendererVisible
      ) {
        this.cssRenderer.domElement.classList.add("css-renderer");
        this.cssRenderer.render(this.activeScene!, this.activeCamera!.instance);
        if (!this.cssRendererAdded) {
          this._bindElement?.appendChild(this.cssRenderer.domElement);
          this.cssRendererAdded = true;
        }
      } else {
        this.cssRenderer.domElement.remove();
        this.cssRendererAdded = false;
      }
    }

    if (this.cssRenderer3d instanceof CSS3DRenderer && this._requestUpdate) {
      if (
        this.hasScene(
          this.activeScene!,
          (element: Object3D) =>
            (element as any).isCSS3DObject && element.visible
        ) &&
        this.cssRendererVisible
      ) {
        this.cssRenderer3d.domElement.classList.add("css-renderer");
        this.cssRenderer3d.render(
          this.activeScene!,
          this.activeCamera!.instance
        );
        if (!this.cssRenderer3dAdded) {
          this._bindElement?.appendChild(this.cssRenderer3d.domElement);
          this.cssRenderer3dAdded = true;
        }
      } else {
        this.cssRenderer3d.domElement.remove();
        this.cssRenderer3dAdded = false;
      }
    }
    this._requestUpdate = false;

    if (this.renderer.instance instanceof WebGLRenderer) {
      this._render_targets.forEach((rt) => {
        rt.beforeRender(rt.scene!, rt.camera!);

        this.renderer.instance.setRenderTarget(rt.rtinstance);

        this.renderer.instance.setSize(
          rt.rtinstance.width,
          rt.rtinstance.height
        );

        this.renderer.render(rt.scene as Scene, rt.camera!);

        this.renderer.instance.setRenderTarget(null!);
        this.renderer.instance.setSize(this._width, this._height);
      });
    }
  }
}

const $engineKey = Symbol("engine");

export class Entity extends Object3D {
  private [$engineKey]: Engine | null;
  protected _enabled: boolean = true;
  protected _initialized: boolean = false;

  constructor() {
    super();
    this[$engineKey] = Engine.activeEngine;
  }

  get enabled() {
    return this._enabled;
  }

  set enabled(value) {
    if (this._enabled != value)
      if (value) this.onEnabled();
      else this.onDisabled();

    this._enabled = value;
  }

  get initialized() {
    return this._initialized;
  }

  set initialized(value: boolean) {
    this._initialized = value;
  }

  begin() {}

  onAdd(engine: Engine) {}
  onRemove(engine: Engine) {}
  onEnabled() {}
  onDisabled() {}

  beforeAnimate() {}

  animate(dt: number, scene?: Scene, camera?: CameraInterface) {}

  beforeRender() {}

  end() {}

  dispose() {
    this.engine?.removeEntity(this);
  }

  get engine(): Engine | null {
    return this[$engineKey] as Engine;
  }
}

export interface CameraInterface {
  get instance(): Camera;
  get controller(): any | undefined;
  update(width?: number, height?: number): void;
  fit(object: Object3D | { center: Vector3; radius: number }):
    | {
        position: Vector3;
        target: Vector3;
      }
    | undefined;
  unproject(point: Vector3): void;
  addEventListener(name: string, func: (event: any) => void): void;
  removeEventListener(name: string, func: (event: any) => void): void;
}

export class BasicCamera implements CameraInterface {
  private _camera?: PerspectiveCamera;
  private _width?: number;
  private _height?: number;

  constructor(width?: number, height?: number) {
    this._width = width;
    this._height = height;
    if (!this._width) this._width = window.innerWidth;
    if (!this._height) this._height = window.innerHeight;
    this._camera = new PerspectiveCamera(
      25,
      this._width / this._height,
      CAMERA_NEAR,
      CAMERA_FAR
    );
    this._camera?.updateProjectionMatrix();
  }

  addEventListener(name: string, func: (event: any) => void): void {
    throw new Error("Method not implemented.");
  }

  removeEventListener(name: string, func: (event: any) => void): void {
    throw new Error("Method not implemented.");
  }

  fit(object: Object3D | { center: Vector3; radius: number }) {
    let center = new Vector3();
    let radius = 1;
    if (object instanceof Object3D) {
      const box = new Box3().setFromObject(object, true);
      const sphere = box.getBoundingSphere(new Sphere());
      center = sphere.center;
      radius = sphere.radius;
    } else {
      center = object.center;
      radius = object.radius;
    }

    let fov = this._camera!.fov * (Math.PI / 180) * 1.0;
    let distance = radius / Math.sin(fov * 0.5);

    let difvector = this._camera!.position.clone()
      .sub(center)
      .normalize()
      .multiplyScalar(distance);

    let newPosition = center.clone().add(difvector);

    this._camera?.position.copy(newPosition);
    this._camera?.lookAt(center);
    this._camera?.updateProjectionMatrix();
    return { position: newPosition, target: center };
  }

  get instance(): Camera {
    return this._camera!;
  }

  get controller() {
    return null;
  }

  update(width?: number, height?: number): void {
    if (this._width) this._width = width;
    if (this._height) this._height = height;
    this._camera!.aspect = this._width! / this._height!;
    this._camera?.updateProjectionMatrix();
  }

  unproject(point: Vector3): void {}
}

export class RenderTarget {
  camera?: Camera;
  scene?: Object3D;
  rtinstance: WebGLRenderTarget;
  beforeRender = (scene: Object3D, camera: Camera) => {};
  afterRender = (scene: Object3D, camera: Camera) => {};

  constructor(
    scene: Object3D,
    camera: Camera,
    { width = 1024, height = 1024, options = {} } = {},
    beforeRender = (scene: Object3D, camera: Camera) => {},
    afterRender = (scene: Object3D, camera: Camera) => {}
  ) {
    this.camera = camera;
    this.scene = scene;
    this.rtinstance = new WebGLRenderTarget(width, height, options);
    this.beforeRender = beforeRender;
    this.afterRender = afterRender;
  }

  dispose() {
    this.scene = undefined;
    this.camera = undefined;
    this.rtinstance.dispose();
  }
}

type Constructor<T = {}> = new (...args: any[]) => T;

export function registerEvents(...events: string[]) {
  return function <T extends Constructor<Entity>>(Base: T) {
    return class extends Base {
      private __registerevents: string[] = [];
      private __registeredevents: any = {};

      constructor(...args: any[]) {
        super(...args);
        this.__registeredevents = [...args];
        this.__registerevents = events;
      }

      onAdd(engine: Engine): void {
        super.onAdd(engine);
        for (const type of this.__registerevents) {
          const binding = ((e: any) => {
            const methodName =
              "on" +
              type.split("-").reduce((prev, cur) => {
                return prev + cur[0].toUpperCase() + cur.slice(1);
              }, "") +
              "Occured";
            const handler = (this as any)[methodName];
            if (typeof handler === "function") handler.call(this, e);
          }).bind(this);
          if (
            type === "dragover" ||
            type === "drop" ||
            type == "click" ||
            type == "pointerdown" ||
            type == "pointermove" ||
            type == "pointerup"
          ) {
            engine.canvas.addEventListener(type, binding);
          } else if (type === "keyup" || type === "keydown") {
            window.addEventListener(type, binding);
          } else {
            engine.addEventListener(type, binding);
          }

          this.__registeredevents[type] = binding;
        }
      }

      onRemove(engine: Engine): void {
        super.onRemove(engine);
        for (const type of Object.keys(this.__registeredevents)) {
          if (
            type === "dragover" ||
            type === "drop" ||
            type === "click" ||
            type === "pointerdown" ||
            type === "pointermove" ||
            type === "pointerup"
          ) {
            engine.canvas.removeEventListener(
              type,
              this.__registeredevents[type]
            );
          } else if (type === "keyup" || type === "keydown") {
            window.removeEventListener(type, this.__registeredevents[type]);
          } else {
            engine.removeEventListener(type, this.__registeredevents[type]);
          }
        }
        this.__registeredevents = [];
      }

      set enabled(value: boolean) {
        if (super.enabled == value) {
          return;
        }
        super.enabled = value;
        if (value) this.onAdd(this.engine!);
        else this.onRemove(this.engine!);
      }
    };
  };
}
